import {Environments} from "@paddle/paddle-js";
import React from "react";
import {Backpack, Filter, History, Regex, Server, Shield, Sparkles} from "lucide-react";

export const PADDLE_CLIENT_TOKEN = "live_7d4f38d1b861c015f0e966c3f62"
export const PADDLE_ENV: Environments = "production"

export const PRICING_PLANS: Plan[] = [
    {
        name: "Personal",
        features: [
            "2 Servers",
            "2 Moderators",
            "2 Console Filters",
            "Live Map",
            "Live Console & Chat",
            "Unlimited Players",
            // "X Sign Logging",
            "X Player Inventory Viewer",
            // "X 3D Spectator Mode (coming soon)",
            "X Regex Console Filters",
            // "X Moderator Logs",
            // "X Advanced Moderation Tools (Notes, Strikes)"
        ],
        featured: false,
        productId: "pro_01m1pdmnq3jab7dq5dvh43950x",
        monthly: "pri_01m1pdzax5djfmra87tww1me37",
        yearly: "pri_01m1pe0akw478bd1jeyr7vfnhd"
    },
    {
        name: "Team",
        features: [
            "5 Servers",
            "10 Moderators",
            "10 Console Filters",
            "Live Map",
            "Live Console & Chat",
            "Unlimited Players",
            // "Sign Logging",
            "Player Inventory Viewer",
            // "3D Spectator Mode (coming soon)",
            "X Regex Console Filters",
            // "X Moderator Logs",
            // "X Advanced Moderation Tools (Notes, Strikes)"
        ],
        featured: true,
        productId: "pro_01m1pe1sn8j2h02tp49rxx93mz",
        monthly: "pri_01m1pe6tg2g3m8hgy1e22ks914",
        yearly: "pri_01m1pe848scycqerk7h9j0411w"
    },
    {
        name: "Organization",
        features: [
            "20 Servers",
            "100 Moderators",
            "20 Console Filters",
            "Live Map",
            "Live Console & Chat",
            "Unlimited Players",
            // "Sign Logging",
            "Player Inventory Viewer",
            // "3D Spectator Mode (coming soon)",
            "Regex Console Filters",
            // "Moderator Logs",
            // "Advanced Moderation Tools (Notes, Strikes)"
        ],
        featured: false,
        productId: "pro_01m1pec60panj6yxy7qs3crbef",
        monthly: "pri_01m1pedhwyasdddcz3ggn72d4w",
        yearly: "pri_01m1pee766rx74d0bz9db3fn50"
    },
]

export function getPlanByProductId(productId?: string): Plan | undefined {
    if (!productId) return undefined
    return PRICING_PLANS.find(p => p.productId === productId)
}

export interface Plan {
    name: string;
    icon?: string; // You can use this for displaying an icon next to the tier name
    features: string[];
    featured: boolean;
    productId: string;
    monthly?: string;
    yearly?: string;
}

// Copy displayed in SubscriptionLimitOverlay when a backend feature limit is hit.
// Keys must match the `Feature` enum values sent by the backend (see Lib/Subscriptions/Feature.cs).
export interface FeatureCopy {
    title: string;
    description: (limit: number, planName: string) => string;
    formatValue: (value: number) => string;
    valueLabel: string;
    Icon: React.ComponentType<{ className?: string }>;
}

export const DEFAULT_FEATURE_COPY: FeatureCopy = {
    title: "You've reached a limit on your current plan",
    description: (limit, planName) =>
        `You've hit the ${limit} limit included in your ${planName} plan. Upgrade to unlock more.`,
    formatValue: (v) => v.toString(),
    valueLabel: "Limit",
    Icon: Sparkles,
}

export const FEATURE_COPY: Record<string, FeatureCopy> = {
    ServerCountOwned: {
        title: "Manage more Rust servers from one place",
        description: (limit, planName) =>
            `Your ${planName} plan supports up to ${limit} ${limit === 1 ? "server" : "servers"}. Upgrade to add more servers and manage them all from a single dashboard.`,
        formatValue: (v) => v.toString(),
        valueLabel: "Servers",
        Icon: Server,
    },
    ConsoleFilterCount: {
        title: "Create more console filters",
        description: (limit, planName) =>
            `Console filters keep noise out of your server logs. Your ${planName} plan allows ${limit} ${limit === 1 ? "filter" : "filters"} — upgrade to create more.`,
        formatValue: (v) => v.toString(),
        valueLabel: "Filters",
        Icon: Filter,
    },
    AllowConsoleFilterRegex: {
        title: "Use regular expressions in console filters",
        description: (_limit, planName) =>
            `Match complex patterns in your server console with full regex support. Regex filters aren't included in the ${planName} plan — upgrade to use them.`,
        formatValue: (v) => (v > 0 ? "Yes" : "No"),
        valueLabel: "Regex",
        Icon: Regex,
    },
    ModeratorCount: {
        title: "Invite more moderators to your team",
        description: (limit, planName) =>
            limit === 0
                ? `Share the workload by inviting trusted moderators to help manage your server. The ${planName} plan doesn't include team moderators — upgrade to start delegating.`
                : `Your ${planName} plan supports ${limit} ${limit === 1 ? "moderator" : "moderators"}. Upgrade to grow your moderation team and scale your community.`,
        formatValue: (v) => v.toString(),
        valueLabel: "Moderators",
        Icon: Shield,
    },
    AuditLog: {
        title: "Track every moderation action with the audit log",
        description: (_limit, planName) =>
            `See who kicked, banned, or unbanned whom — with full timestamps and context. The audit log isn't included in the ${planName} plan; upgrade to keep your team accountable.`,
        formatValue: (v) => (v > 0 ? "Yes" : "No"),
        valueLabel: "Audit log",
        Icon: History,
    },
    InventoryViewer: {
        title: "See exactly what your players are carrying",
        description: (_limit, planName) =>
            `Inspect any player's main, belt, and worn items in real time. The inventory viewer isn't included in the ${planName} plan — upgrade to view player inventories.`,
        formatValue: (v) => (v > 0 ? "Yes" : "No"),
        valueLabel: "Inventory viewer",
        Icon: Backpack,
    },
}